import React, { useMemo, useState, useEffect, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { isUndefined, get, forEach, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import dayjs from 'dayjs';
import { ColumnSort } from 'legacy-packages/ft-cb';
import cn from 'classnames';
import snakeCase from 'lodash/snakeCase';
import keyBy from 'lodash/keyBy';
import {
  getMCQScoreByUsers,
  getFRQScoreByUsers,
} from 'legacy-src/utility/responseAnalysis';

import dateTimeFormat from 'legacy-src/utility/DateTime';
import {
  learnosityReportByUsers,
  getScoreTimeSpents,
  averageTimeSpentInSeconds,
  secondsToTime,
} from 'legacy-src/utility/progressHelpers';

import { isStudentSubmissionLate } from 'chameleon/ui-stack/ui/assessments/util';

import { TEI_TYPES } from 'legacy-src/constants/assignment';
import { getPriorStudentsSubdir } from 'legacy-src/utility/utils';
import Tooltip from '@material-ui/core/Tooltip';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import { withStyles } from '@material-ui/core/styles';
import sortBy from 'lodash/sortBy';
import { calculateResponseDistribution } from 'chameleon-src/components/results/resultsQuestions/utils';
import ResponseDistribution from 'chameleon-src/components/results/resultsQuestions/ResponseDistribution';
import { QuestionTypes } from 'legacy/packages/unit-outline/src/constants';
import TableSorter from './tableSorter';

import './index.scss';
import PerformanceMarker from '../PerformanceMarker';
import usePerformanceTiers from 'chameleon/src/hooks/usePerformanceTiers';

const HELPER_STRING =
  'The number of minutes between when the student started and submitted the assignment.';
const NO_RESULTS_LABEL = 'No results';
const NO_ANSWER_LABEL = '-';

const icons = {
  correct: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width="16"
      height="16"
      viewBox="0 0 16 16"
    >
      <path
        fill="#000"
        fillRule="nonzero"
        stroke="#000"
        d="M6.542 12.707L2 8.166l1.37-1.36 3.172 3.172 7.03-7.03 1.369 1.36z"
      />
    </svg>
  ),
  incorrect: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width="16"
      height="16"
      viewBox="0 0 16 16"
    >
      <path
        fill="#000"
        fillRule="nonzero"
        stroke="#000"
        d="M14.5 2.986L13.014 1.5 8 6.524 2.986 1.5 1.5 2.986 6.524 8 1.5 13.014 2.986 14.5 8 9.476l5.014 5.024 1.486-1.486L9.476 8z"
      />
    </svg>
  ),
};

const HelpToolTip = (props) => {
  const { content } = props;
  const [visible, setVisible] = useState(false);
  const LightTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 11,
    },
    arrow: {
      color: theme.palette.common.black,
    },
  }))(Tooltip);
  return (
    <LightTooltip
      title={content}
      open={visible}
      placement="bottom"
      arrow // not sure why, but arrow does not display.
    >
      <button
        onClick={() => setVisible(!visible)}
        className={cn(
          'pop_over_toggle',
          'btn',
          `btn--shape_round`,
          `btn--size_xsmall`,
        )}
      >
        ?
      </button>
    </LightTooltip>
  );
};

const TimeTakenHelp = () => (
  <div className="TimeTakenHelpPopover">
    <HelpToolTip
      content={
        <p style={{ padding: '10px', fontSize: '14px', lineHeight: '1.43' }}>
          {HELPER_STRING}
        </p>
      }
    />
  </div>
);

const renderTimeTaken = (assignmentTimer, timeTaken) => {
  let className = 'td_time';
  let displayedValue = '-';
  if (timeTaken) {
    if (timeTaken > 200 || timeTaken > assignmentTimer) {
      className = 'td_time_over_limit';
    }
    displayedValue = timeTaken > 200 ? '200+' : timeTaken;
  }
  return <td className={className}>{displayedValue}</td>;
};

const MatrixColumSort = ({ children, ...props }) => (
  <ColumnSort {...props} isDisabled={false} sortIconColor="black">
    {children}
  </ColumnSort>
);

const renderHeaderOneColumn = (item, questionPreviewProps = {}) => {
  const { id, title, learnosityItemReference } = item;
  return (
    <span
      {...questionPreviewProps}
      data-itemid={id}
      data-reference={learnosityItemReference}
      data-ypos="top"
      role="link"
      tabIndex="0"
    >
      {title}
    </span>
  );
};

const renderResponseColumnSort = (item, otherProps, isChildColumn = false) => {
  const MCQ = 'MCQ';
  const FRQ = 'FRQ';
  let itemType = MCQ;

  if (
    item.type === 'ScoringRubricCategory' ||
    item.type === 'FreeResponseQuestion'
  ) {
    itemType = FRQ;
  }

  if (otherProps.showTwoColumnResults && itemType !== MCQ) {
    return (
      <span
        {...otherProps.questionPreviewProps}
        data-itemid={item.id}
        data-reference={item.learnosityItemReference}
        data-ypos={isChildColumn ? 'bottom' : 'top'}
        role="link"
        tabIndex="0"
      >
        {item.title}
      </span>
    );
  }

  return (
    <MatrixColumSort
      columnId={`responses_${itemType}_${item.learnosityItemReference}`}
      {...otherProps}
    >
      <span
        {...otherProps.questionPreviewProps}
        data-itemid={item.id}
        data-reference={item.learnosityItemReference}
        data-ypos={isChildColumn ? 'bottom' : 'top'}
        role="link"
        tabIndex="0"
        className="whitespace-normal text-ellipsis line-clamp-3 max-h-24"
      >
        {item.title}
      </span>
      <br />
    </MatrixColumSort>
  );
};

const StudentPerformanceTable = (props) => {
  const {
    items,
    params,
    students: groupStudents,
    routes,
    assignment,
    learnosityReportsData,
    learnosityReportsByItemData,
    learnosityReportsByRubricData,
    sessionTemplate,
    submittedDateStudentMatrix,
    submissionData,
    isPreApSubject,
    hidePointsEarned = false,
    showScoringDropdown,
    selectedScoringDropdownItem,
    studentScoringResults,
    teacherScoringResults,
    setDropdownPosition,
    pacingFlag,
    resultsDistractorsFlag,
    userData,
    studentResponses,
    scoringRubricResponses,
    questionPreviewProps,
    selectedView,
    isPrint,
    setIsPrint,
    setResultsOverviewCSVData,
    allowLateSubmissionFF,
  } = props;

  const { performanceTiers } = usePerformanceTiers(params.subject);

  const isTimeView = () => selectedView === 'time';
  const isAnswerView = () => selectedView === 'answer';

  const { averageTimeSpents, userTimeSpents, userTimeSpentPerQuestion } =
    getScoreTimeSpents(learnosityReportsByItemData);

  const { students: assignmentStudents, customAssignmentSettings } = assignment;
  const students = sortBy(assignmentStudents || groupStudents, [
    'lastName',
    'firstName',
  ]);

  const customSettingsByStudentId = useMemo(
    () =>
      keyBy(
        customAssignmentSettings.map((item) =>
          Object.entries(item).reduce((acc, [key, value]) => {
            acc[snakeCase(key)] = value;
            return acc;
          }, {}),
        ),
        'user_id',
      ),
    [customAssignmentSettings],
  );

  const [tableSortSelection, setTableSortSelection] = useState('');
  const [studentItems, setStudentItems] = useState(students);
  const [rowSpan, setRowSpan] = useState(2);
  const [headerTwoRowSpan, setHeaderTwoRowSpan] = useState(1);

  const studentsTable = useRef();
  const tableSorter = useRef(null);

  useEffect(() => {
    setIsPrint(assignment.printToggle);
  }, [assignment]);

  useEffect(() => {
    if (isTimeView()) {
      const elems = document.getElementsByClassName('student_scoring_header');
      forEach(elems, (elem) => {
        elem.style.removeProperty('top');
      });
    }
  }, [selectedView]);

  useEffect(() => {
    const studentsTableHasSecondHeader = () => {
      const tableElement = studentsTable.current;
      const secondLevelHeaders = tableElement?.querySelectorAll(
        'thead tr:nth-child(2) th',
      );

      return Boolean(secondLevelHeaders && secondLevelHeaders.length);
    };

    /**
     * Update the second header `top` position in order to have it sticky
     * related to first level header.
     * */
    const updateTableSecondHeaderTopPosition = () => {
      const tableElement = studentsTable.current;
      const firstLevelHeader = tableElement.querySelector(
        'thead tr:first-child th[rowspan="1"]',
      );
      const firstLevelHeaderHeight =
        firstLevelHeader.getBoundingClientRect().height;
      const secondLevelHeaders = tableElement.querySelectorAll(
        'thead tr:nth-child(2) th',
      );
      forEach(
        secondLevelHeaders,
        (secondLevelHeader) =>
          (secondLevelHeader.style.top = `${firstLevelHeaderHeight}px`),
      );
    };

    const updateStudentScoringHeaderTopPosition = () => {
      const tableElement = studentsTable.current;
      const hasSecondHeader = studentsTableHasSecondHeader();
      let headerHeight;
      const headerOneHeght = tableElement
        .querySelector('thead tr:first-child')
        .getBoundingClientRect().height;
      const headerTwoHeight = tableElement
        .querySelector('thead tr:nth-child(2)')
        .getBoundingClientRect().height;

      if (hasSecondHeader) {
        headerHeight = headerOneHeght + headerTwoHeight;
      } else {
        headerHeight = headerOneHeght;
      }

      const studentScoringHeaders = tableElement.querySelectorAll(
        `thead ${hasSecondHeader ? 'tr:nth-child(3)' : 'tr:nth-child(2)'} th`,
      );
      forEach(studentScoringHeaders, (header) =>
        header.setAttribute('style', `top:${headerHeight}px !important`),
      );
    };

    const updateDropdownPosition = () => {
      const tableElement = studentsTable.current;
      const firstLevelHeader = tableElement.querySelectorAll(
        'thead tr:first-child th',
      );
      let widthSum = 0;

      for (let index = 0; index < firstLevelHeader.length; index++) {
        const header = firstLevelHeader[index];
        if (header.className === 'question_header') break;
        widthSum += header.getBoundingClientRect().width;
      }

      setDropdownPosition(widthSum);
    };

    if (studentsTableHasSecondHeader()) {
      updateTableSecondHeaderTopPosition();
      window.addEventListener('resize', updateTableSecondHeaderTopPosition);
      window.addEventListener(
        'orientationchange',
        updateTableSecondHeaderTopPosition,
      );
    }

    if (showScoringDropdown) {
      updateStudentScoringHeaderTopPosition();
      window.addEventListener('resize', updateStudentScoringHeaderTopPosition);
      window.addEventListener(
        'orientationchange',
        updateStudentScoringHeaderTopPosition,
      );

      updateDropdownPosition();
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('orientationchange', updateDropdownPosition);
    }

    return () => {
      if (studentsTableHasSecondHeader()) {
        window.removeEventListener(
          'resize',
          updateTableSecondHeaderTopPosition,
        );
        window.removeEventListener(
          'orientationchange',
          updateTableSecondHeaderTopPosition,
        );
      }

      if (showScoringDropdown) {
        window.removeEventListener(
          'resize',
          updateStudentScoringHeaderTopPosition,
        );
        window.removeEventListener(
          'orientationchange',
          updateStudentScoringHeaderTopPosition,
        );

        window.removeEventListener('resize', updateDropdownPosition);
        window.removeEventListener('orientationchange', updateDropdownPosition);
      }
    };
  });

  // set results overview CSV report data
  useEffect(() => {
    const { assessment, hasStudentScores } = assignment;
    const { isPracticeExam } = assessment;

    const resultsOverviewReportHeaders = [
      'Assignment Name',
      'Assignment Type',
      'Class',
      'External Student Id',
      'Student Last Name',
      'Student First Name',
      'Student Preferred Name',
      'Points Earned',
      'Points Possible',
    ];

    // Add Due Date and Submitted Late column only when the FF is on
    if (allowLateSubmissionFF) {
      resultsOverviewReportHeaders.push(
        'Assignment Due Date',
        'Date Submitted',
        'Submitted Late?',
      );
    } else {
      resultsOverviewReportHeaders.push('Date Submitted');
    }

    // Append the item titles to the headers
    items.forEach((item) => {
      if (item.children) {
        item.children.forEach((child) => {
          if (hasStudentScores) {
            // append columns for both student and teacher scores
            resultsOverviewReportHeaders.push(
              `${item.title} ${child.title} Student`,
            );
            resultsOverviewReportHeaders.push(
              `${item.title} ${child.title} Teacher`,
            );
          } else {
            resultsOverviewReportHeaders.push(`${item.title} ${child.title}`);
          }
        });
      } else {
        resultsOverviewReportHeaders.push(item.title);
      }
    });

    const resultsOverviewReport = submissionData.reduce(
      (
        acc,
        {
          user_id,
          school_student_id,
          last_name,
          first_name,
          preferred_first_name,
        },
      ) => {
        const assignmentType = assessment.isPpc
          ? 'Progress Checks'
          : isPracticeExam
            ? 'Practice Exams'
            : 'My Quizzes';
        const studentRow = [];
        studentRow.push(assignment.title);
        studentRow.push(assignmentType);
        studentRow.push(assignment.sectionName);
        studentRow.push(school_student_id);
        studentRow.push(last_name);
        studentRow.push(first_name);
        studentRow.push(preferred_first_name);

        let total = 0;
        let possible = 0;
        const studentResults = resultsByUser[user_id];
        if (studentResults) {
          total = parseFloat(studentResults.score);
          possible = parseFloat(studentResults.max_score);
        }
        studentRow.push(total);
        studentRow.push(possible);

        const submittedTime = !studentSessionsByUserId[user_id]?.submittedAt
          ? null
          : dateTimeFormat(
              dayjs.utc(studentSessionsByUserId[user_id]?.submittedAt),
              'dateAndTime',
              assignment.timezone,
            );

        if (allowLateSubmissionFF) {
          // Add Due Date column based on CAS
          let dueAt = get(customSettingsByStudentId, `${user_id}.due_at`);
          if (isUndefined(dueAt)) {
            dueAt = assignment.dueAt;
          }
          dueAt = dateTimeFormat(
            dueAt ? dayjs.utc(dueAt) : null,
            'dateAndTime',
            assignment.timezone,
          );
          const submitted = get(
            studentSessionsByUserId,
            `${user_id}.submittedAt`,
          );
          // Populate column only if there is a submission data
          let submittedLate = '';
          if (!isEmpty(submitted)) {
            const isSubmittedLate = isStudentSubmissionLate({
              submitted,
              customSetting: customSettingsByStudentId[user_id],
              dueAt: assignment.dueAt,
            });
            submittedLate = isSubmittedLate ? 'Yes' : 'No';
          }
          studentRow.push(dueAt, submittedTime, submittedLate);
        } else {
          studentRow.push(submittedTime);
        }

        // Append responses

        items.forEach((item) => {
          if (item.type === 'FreeResponseQuestion') {
            if (item.children) {
              // item has multiple parts
              item.children.forEach((child) => {
                let result;
                if (teacherResultsByUsers[user_id]) {
                  const questions = teacherResultsByUsers[user_id].items;
                  result = questions[child.learnosityItemReference];
                }
                if (hasStudentScores) {
                  const questions =
                    studentResultsByUsers[user_id] &&
                    studentResultsByUsers[user_id].items;
                  const studentResult =
                    questions && questions[child.learnosityItemReference];
                  // append columns for both student and teacher scores
                  studentRow.push(studentResult?.score || NO_ANSWER_LABEL);
                }
                studentRow.push(result?.score || NO_ANSWER_LABEL);
              });
            } else {
              let result;
              if (teacherResultsByUsers[user_id]) {
                const questions = teacherResultsByUsers[user_id].items;
                result = questions[item.learnosityItemReference];
              }
              studentRow.push(result.score);
            }
            return;
          }

          const responseDistributionInfo = calculateResponseDistribution(
            studentResponses,
            sessionTemplate,
            item.learnosityItemReference,
            user_id,
          );

          const { studentResponse } = responseDistributionInfo || {};

          if (studentResponse) {
            studentRow.push(studentResponse);
          } else {
            studentRow.push(NO_ANSWER_LABEL);
          }
        });

        acc.push(studentRow);
        return acc;
      },
      [resultsOverviewReportHeaders],
    );
    setResultsOverviewCSVData(resultsOverviewReport);
  }, [submissionData]);

  const { items: learnosityItems = [] } = sessionTemplate || {};
  const resultsByUser = learnosityReportByUsers(learnosityReportsData);
  const itemResultsByUser = learnosityReportByUsers(
    learnosityReportsByItemData,
  );
  const rubricResultByUser = learnosityReportByUsers(
    learnosityReportsByRubricData,
  );
  const responsesByUser = studentResponses || {};

  const scoringRubricScoresByUser = scoringRubricResponses || {};

  const teacherResultsByUsers = learnosityReportByUsers(teacherScoringResults);
  const studentResultsByUsers = learnosityReportByUsers(studentScoringResults);
  const mcqScoresByUsers = showScoringDropdown
    ? learnosityReportsByItemData &&
      getMCQScoreByUsers(learnosityReportsByItemData, assignment.questions)
    : {};
  const teacherFRQScoresByUsers = getFRQScoreByUsers(teacherScoringResults);
  const studentFRQScoresByUsers = getFRQScoreByUsers(studentScoringResults);

  // We build a list of TEIs reference so that we don't have to
  // map/find through all the items each time.
  const listOfTEIs = [];

  learnosityItems.forEach(
    (item) =>
      item.questions[0] &&
      TEI_TYPES.includes(item.questions[0].type) &&
      listOfTEIs.push(item.reference),
  );

  const timeTakenByStudent = assignment.timeTaken.reduce((acc, timeTaken) => {
    acc[timeTaken.studentId] = timeTaken.seconds
      ? Math.floor(timeTaken.seconds / 60)
      : null;
    return acc;
  }, {});

  const studentSessionsByUserId =
    assignment.studentSessions &&
    assignment.studentSessions.reduce(
      (acc, session) => ({
        ...acc,
        [session.userId]: session,
      }),
      {},
    );

  useEffect(() => {
    tableSorter.current = new TableSorter(
      students,
      resultsByUser,
      studentSessionsByUserId,
      responsesByUser,
      scoringRubricScoresByUser,
      itemResultsByUser,
      rubricResultByUser,
      listOfTEIs,
      isPreApSubject,
      studentResultsByUsers,
      teacherResultsByUsers,
      mcqScoresByUsers,
      teacherFRQScoresByUsers,
      studentFRQScoresByUsers,
      showScoringDropdown,
      selectedScoringDropdownItem,
      selectedView,
      userTimeSpents,
      userTimeSpentPerQuestion,
    );
  }, [students, selectedScoringDropdownItem]);

  const headerOne = [];
  const headerTwo = [];
  const showTwoColumnResults =
    showScoringDropdown &&
    !isEmpty(selectedScoringDropdownItem) &&
    selectedScoringDropdownItem.value === 'all';
  const showStudentColumnResults =
    showScoringDropdown &&
    !isEmpty(selectedScoringDropdownItem) &&
    selectedScoringDropdownItem.value === 'student';
  const showTeacherColumnResults =
    showScoringDropdown &&
    !isEmpty(selectedScoringDropdownItem) &&
    selectedScoringDropdownItem.value === 'teacher';

  const onTableSortChange = (tableSort, isSwitchingView = false) => {
    if (isSwitchingView) {
      tableSort = `${tableSort}_unsorted`;
    }
    const sortedItems = tableSorter.current.sortStudent(tableSort);
    setStudentItems(sortedItems);
    setTableSortSelection(tableSort);
  };

  const otherProps = {
    selection: tableSortSelection,
    onChange: onTableSortChange,
    isPreApSubject,
    sessionTemplate,
    showTwoColumnResults,
    showStudentColumnResults,
    showTeacherColumnResults,
    questionPreviewProps,
  };

  useEffect(() => {
    if (showScoringDropdown) {
      setRowSpan(rowSpan + 1);
    }
  }, []);

  useEffect(() => {
    onTableSortChange('', true);
  }, [selectedView]);

  useEffect(() => {
    if (
      !isEmpty(selectedScoringDropdownItem) &&
      selectedScoringDropdownItem.value !== 'all'
    ) {
      setHeaderTwoRowSpan(2);
    } else {
      setHeaderTwoRowSpan(1);
    }
  }, [selectedScoringDropdownItem]);

  const headerThree = !hidePointsEarned
    ? [
        [
          <MatrixColumSort {...otherProps} columnId="studentScores">
            Student
          </MatrixColumSort>,
          <MatrixColumSort {...otherProps} columnId="teacherScores">
            Teacher
          </MatrixColumSort>,
        ],
      ]
    : [];

  items.forEach((item) => {
    const childCount = item.children ? item.children.length : 0;
    const collapseChild =
      childCount === 1 && item.children[0].title === item.title;
    headerOne.push({
      key: item.learnosityItemReference,
      itemId: item.id,
      children:
        childCount > 0
          ? renderHeaderOneColumn(item, questionPreviewProps)
          : renderResponseColumnSort(item, otherProps),
      rowSpan: childCount === 0 || collapseChild ? 2 : 1,
      colSpan: childCount || 1,
      scope: childCount > 1 ? 'colgroup' : 'col',
      isMcq: childCount === 0 || collapseChild,
    });
    if (childCount > 0 && !collapseChild && !isTimeView()) {
      item.children.forEach((child) => {
        headerTwo.push({
          key: child.learnosityItemReference,
          children: renderResponseColumnSort(child, otherProps, true),
          scope: 'col',
        });

        if (showTwoColumnResults) {
          headerThree.push([
            <MatrixColumSort
              {...otherProps}
              columnId={`studentScoresWithLearnRef_${child.learnosityItemReference}`}
            >
              S
            </MatrixColumSort>,
            <MatrixColumSort
              {...otherProps}
              columnId={`teacherScoresWithLearnRef_${child.learnosityItemReference}`}
            >
              T
            </MatrixColumSort>,
          ]);
        }
      });
    }
  });
  const showTimeTaken = !isPreApSubject && !!assignment.timer;
  const preapStyle = {
    fontWeight: 400,
    backgroundColor: 'white',
    overflow: 'hidden',
  };

  const getScoresFromQuestions = (questions, items) => {
    const noMaxScore = items.some((item) => item.max_score === null);
    if (noMaxScore) {
      const anyFullyScoredStudent = Object.keys(resultsByUser)[0];
      return +resultsByUser[anyFullyScoredStudent].max_score;
    }

    return questions.reduce((acc, question) => acc + question.max_score, 0);
  };

  const getMaxScoreFromByItemDataReport = (report) => {
    const data = get(report, '[0].data');
    if (!isEmpty(data)) {
      const { users } = Object.values(data)[0];
      const firstUserReport = users[Object.keys(users)[0]];

      if (firstUserReport && firstUserReport.items) {
        const itemsArray = Object.values(firstUserReport.items);
        return getScoresFromQuestions(itemsArray, itemsArray);
      }
    }
  };

  const maxScore = getMaxScoreFromByItemDataReport(learnosityReportsByItemData);

  const NO_SCORE = { score: 0, maxScore: 0, userNotScored: true };

  const getTwoColumnTableData = (studentId, learnosityItemReference) => {
    const defaultData = { score: '-', className: 'no_data' };

    const getData = (resultsByUsers) => {
      if (resultsByUsers[studentId]) {
        const questions = resultsByUsers[studentId].items;
        const result = questions[learnosityItemReference];

        if (result) {
          const className = result.correct ? 'correct' : 'incorrect';
          return {
            score: isAnswerView() ? result.score : result.time_spent || '-',
            className,
          };
        }

        return defaultData;
      }

      return defaultData;
    };

    return [getData(studentResultsByUsers), getData(teacherResultsByUsers)];
  };

  const getSingleColumnTableData = (
    resultsByUsers,
    studentId,
    learnosityItemReference,
  ) => {
    const defaultData = (
      <td className="student_scoring_data no_data">
        <div className="student_scoring_data_items">-</div>
      </td>
    );

    if (resultsByUsers[studentId]) {
      const questions = resultsByUsers[studentId].items;
      const result = questions[learnosityItemReference];

      if (result) {
        return (
          <td
            className={cn('student_scoring_data', {
              correct: result.correct,
              incorrect: !result.correct,
            })}
          >
            <div className="student_scoring_data_items">{result.score}</div>
          </td>
        );
      }

      return defaultData;
    }

    return defaultData;
  };

  const isTeacherOrAdminOrCoordinator =
    userData.isTeacher || userData.isAdmin || userData.isCoordinator;
  const studentScoringCondition =
    !isPreApSubject && isTeacherOrAdminOrCoordinator;

  const isAllFRQ = items.every(
    (item) => item.type === QuestionTypes.FREE_RESPONSE_QUESTION,
  );

  return (
    <div>
      <div className="StudentPerformanceTable">
        <div className="table-wrap">
          <table
            ref={studentsTable}
            className={cn('table--lined', {
              student_scoring: showScoringDropdown,
            })}
          >
            <thead>
              <tr>
                <th
                  scope="col"
                  rowSpan={rowSpan}
                  className="th_name"
                  style={isPreApSubject ? preapStyle : undefined}
                >
                  <MatrixColumSort columnId="name" {...otherProps}>
                    Name
                  </MatrixColumSort>
                </th>
                {!hidePointsEarned && (
                  <th
                    scope="col"
                    rowSpan="2"
                    className="th_points"
                    style={isPreApSubject ? preapStyle : undefined}
                  >
                    {showScoringDropdown ? (
                      <Fragment>Points Earned (of {maxScore})</Fragment>
                    ) : (
                      <MatrixColumSort columnId="pointEarned" {...otherProps}>
                        Points Earned <br />
                        (of {maxScore}
                        {isPreApSubject && ' pts'})
                      </MatrixColumSort>
                    )}
                  </th>
                )}
                {pacingFlag && !isPrint && (
                  <th scope="col" rowSpan={rowSpan} className="th_time">
                    <MatrixColumSort columnId="timeSpent" {...otherProps}>
                      Total time spent
                    </MatrixColumSort>
                  </th>
                )}
                {showTimeTaken && !pacingFlag && (
                  <th scope="col" rowSpan={rowSpan} className="th_time">
                    Time Taken <TimeTakenHelp />
                  </th>
                )}
                {!isPreApSubject && submittedDateStudentMatrix && (
                  <th scope="col" rowSpan={rowSpan} className="th_submitted">
                    <MatrixColumSort columnId="submitted" {...otherProps}>
                      {(isPrint && showScoringDropdown) ||
                      studentScoringCondition
                        ? 'Submitted'
                        : isPrint
                          ? 'Scored'
                          : 'Submitted'}
                    </MatrixColumSort>
                  </th>
                )}
                {headerOne.map(({ isMcq, ...h }) => (
                  <th
                    key={h.key}
                    {...h}
                    style={isPreApSubject ? preapStyle : undefined}
                    rowSpan={isMcq ? h.rowSpan + 1 : h.rowSpan}
                    className="question_header"
                  />
                ))}
              </tr>
              <tr>
                {headerTwo.map((h) => (
                  <th
                    key={h.key}
                    {...h}
                    style={
                      isPreApSubject
                        ? { ...preapStyle, textAlign: 'center' }
                        : undefined
                    }
                    rowSpan={headerTwoRowSpan}
                    className="hover-question-parent"
                  />
                ))}
              </tr>
              {showScoringDropdown && (
                <tr>
                  {headerThree.map((h) => (
                    <th className="student_scoring_header" scope="colgroup">
                      <div className="student_scoring_header_items">
                        {h.map((innerHeader) => (
                          <div className="item">{innerHeader}</div>
                        ))}
                      </div>
                    </th>
                  ))}
                </tr>
              )}
              {resultsDistractorsFlag && !isPrint && !isAllFRQ && <tr />}
              {resultsDistractorsFlag && !isPrint && !isAllFRQ && (
                <tr>
                  <th className="average_time_spent_header">
                    MOST COMMON DISTRACTOR
                  </th>
                  <th colSpan={3} className="average_time_spent_filler" />
                  {items.map((item) => {
                    if (item.type === QuestionTypes.FREE_RESPONSE_QUESTION) {
                      return (
                        <td
                          colSpan={item.children ? item.children.length : 1}
                          key={item.id}
                          className="question_header average_time_spent_cell"
                        >
                          <span>-</span>
                        </td>
                      );
                    }
                    const responseDistributionInfo =
                      calculateResponseDistribution(
                        studentResponses,
                        sessionTemplate,
                        item.learnosityItemReference,
                      );

                    const {
                      rightAnswer,
                      mostCommonDistractors,
                      responseDistribution,
                    } = responseDistributionInfo;

                    const responseDistributionComponent = (
                      <ResponseDistribution
                        responseDistribution={responseDistribution}
                        rightAnswer={rightAnswer}
                      />
                    );

                    const mostCommonDistractor = mostCommonDistractors ? (
                      <span>
                        {mostCommonDistractors.length
                          ? mostCommonDistractors.join(', ')
                          : 'NA'}
                      </span>
                    ) : null;

                    return (
                      <td
                        colSpan={item.children ? item.children.length : 1}
                        key={item.id}
                        className="question_header average_time_spent_cell"
                      >
                        <PopOver
                          description={responseDistributionComponent}
                          containerClassName="tooltip-label-container"
                          trigger={mostCommonDistractor}
                          popOnHover
                          position="top-center"
                          size="auto"
                        >
                          {responseDistributionComponent}
                        </PopOver>
                      </td>
                    );
                  })}
                </tr>
              )}
              {pacingFlag && !isPrint && (
                <React.Fragment>
                  <tr />
                  <tr>
                    <th className="average_time_spent_header">
                      Average time spent
                    </th>
                    <th colSpan={3} className="average_time_spent_filler" />
                    {items.map((item) => (
                      <td
                        colSpan={item.children ? item.children.length : 1}
                        key={item.id}
                        className="question_header average_time_spent_cell"
                      >
                        {secondsToTime(
                          averageTimeSpentInSeconds(
                            averageTimeSpents[item.learnosityItemReference],
                          ),
                        )}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              )}
            </thead>
            <tbody>
              {studentItems.map((student) => {
                let total = 0;
                let possible = 0;
                const studentResults = resultsByUser[student.id];
                if (studentResults) {
                  total = parseFloat(studentResults.score);
                  possible = parseFloat(studentResults.max_score);
                }

                const includeAsterisk = submissionData.some(
                  (submission) =>
                    submission.user_id.toString() === student.id.toString() &&
                    submission.is_copied === true,
                )
                  ? '*'
                  : '';

                const performanceBarLayout = ['totalOverall'];
                if (!isPreApSubject) {
                  performanceBarLayout.push('verticalBar');
                }
                const preapResultsStyle = {
                  ...preapStyle,
                  borderLeft: '0.1rem solid #e8e8e8',
                  borderTop: ' inherit',
                };

                const MCQScores =
                  mcqScoresByUsers && mcqScoresByUsers[student.id]
                    ? mcqScoresByUsers[student.id]
                    : NO_SCORE;
                const teacherFRQScore =
                  teacherFRQScoresByUsers && teacherFRQScoresByUsers[student.id]
                    ? teacherFRQScoresByUsers[student.id]
                    : NO_SCORE;
                const studentFRQScore =
                  studentFRQScoresByUsers && studentFRQScoresByUsers[student.id]
                    ? studentFRQScoresByUsers[student.id]
                    : NO_SCORE;
                const overallTeacherScore = teacherFRQScore.userNotScored
                  ? '-'
                  : MCQScores.score + teacherFRQScore.score;
                const overallStudentScore = studentFRQScore.userNotScored
                  ? '-'
                  : MCQScores.score + studentFRQScore.score;

                return (
                  <tr
                    key={student.id}
                    style={isPreApSubject ? preapStyle : undefined}
                  >
                    <th style={isPreApSubject ? preapStyle : undefined}>
                      {itemResultsByUser[student.id] ? (
                        <Link
                          to={`/${params.subject}/assessments/results/${
                            params.assignmentId
                          }${getPriorStudentsSubdir(routes)}/students/${
                            student.id
                          }`}
                        >
                          {student.firstName} {student.lastName}
                        </Link>
                      ) : (
                        <span className="ml-2">
                          {student.firstName} {student.lastName}
                        </span>
                      )}
                    </th>
                    {!hidePointsEarned &&
                      (showScoringDropdown ? (
                        <td className="student_scoring_data">
                          <div className="student_scoring_data_items">
                            <div
                              className={
                                studentFRQScore.userNotScored
                                  ? 'no_data'
                                  : 'overall_scores'
                              }
                            >
                              <PerformanceMarker
                                performanceTiers={
                                  isPreApSubject ? [] : performanceTiers
                                }
                                showPointsPossible={false}
                                points={{
                                  total: overallStudentScore,
                                  possible: maxScore,
                                }}
                                layout={performanceBarLayout}
                                showScoringDropdown={showScoringDropdown}
                                studentScoringCondition={
                                  studentScoringCondition
                                }
                              />
                            </div>
                            <div
                              className={
                                teacherFRQScore.userNotScored
                                  ? 'no_data'
                                  : 'overall_scores'
                              }
                            >
                              <PerformanceMarker
                                performanceTiers={
                                  isPreApSubject ? [] : performanceTiers
                                }
                                showPointsPossible={false}
                                points={{
                                  total: overallTeacherScore,
                                  possible: maxScore,
                                }}
                                layout={performanceBarLayout}
                                showScoringDropdown={showScoringDropdown}
                                studentScoringCondition={
                                  studentScoringCondition
                                }
                              />
                            </div>
                          </div>
                        </td>
                      ) : (
                        <td
                          style={
                            isPreApSubject
                              ? {
                                  ...preapStyle,
                                  fontWeight: 700,
                                }
                              : undefined
                          }
                        >
                          <PerformanceMarker
                            performanceTiers={
                              isPreApSubject ? [] : performanceTiers
                            }
                            showPointsPossible={false}
                            points={{ total, possible }}
                            layout={performanceBarLayout}
                            studentScoringCondition={studentScoringCondition}
                          />
                        </td>
                      ))}
                    {pacingFlag && !isPrint && (
                      <td>{secondsToTime(userTimeSpents[student.id], true)}</td>
                    )}
                    {showTimeTaken &&
                      !pacingFlag &&
                      renderTimeTaken(
                        assignment.timer,
                        timeTakenByStudent[student.id],
                      )}
                    {!isPreApSubject && submittedDateStudentMatrix && (
                      <td className="td_submitted_time">
                        {itemResultsByUser[student.id] ? (
                          <div
                            className={cn('td_submitted_time_content', {
                              student_scoring: showScoringDropdown,
                            })}
                          >
                            {dateTimeFormat(
                              dayjs.utc(
                                studentSessionsByUserId[student.id].submittedAt,
                              ),
                              'dateAndTimeShort',
                              assignment.timezone,
                            )}
                            {includeAsterisk}
                            {isStudentSubmissionLate({
                              submitted:
                                studentSessionsByUserId[student.id].submittedAt,
                              customSetting:
                                customSettingsByStudentId[student.id],
                              dueAt: assignment.dueAt,
                            }) && (
                              <span className="ml-1 font-medium text-red-600">
                                (Late)
                              </span>
                            )}
                          </div>
                        ) : (
                          <div
                            className={cn('td_submitted_time_content', {
                              student_scoring: showScoringDropdown,
                            })}
                          >
                            -
                          </div>
                        )}
                      </td>
                    )}
                    {items.map((item) => {
                      const childCount = item.children
                        ? item.children.length
                        : 0;
                      if (childCount) {
                        const results =
                          (rubricResultByUser[student.id] || {}).items || {};
                        const itemResultsDict = Object.fromEntries(
                          Object.entries(results).filter(([key]) =>
                            key.includes(item.learnosityItemReference),
                          ),
                        );

                        if (isTimeView()) {
                          return (
                            <td
                              colSpan={childCount}
                              className={cn('student_scoring_data', {
                                correct:
                                  Object.values(itemResultsDict).length &&
                                  Object.values(itemResultsDict).every(
                                    ({ correct }) => correct,
                                  ),
                                incorrect:
                                  Object.values(itemResultsDict).length &&
                                  Object.values(itemResultsDict).every(
                                    ({ correct }) => !correct,
                                  ),
                                partial:
                                  Object.values(itemResultsDict).length &&
                                  Object.values(itemResultsDict).some(
                                    ({ correct }) => correct,
                                  ) &&
                                  Object.values(itemResultsDict).some(
                                    ({ correct }) => !correct,
                                  ),
                              })}
                            >
                              <div className="student_scoring_data_items">
                                {userTimeSpentPerQuestion[student.id]
                                  ? secondsToTime(
                                      userTimeSpentPerQuestion[student.id][
                                        item.learnosityItemReference
                                      ],
                                    )
                                  : '-'}
                              </div>
                            </td>
                          );
                        }
                        return item.children.map((child) => {
                          const itemResult =
                            results[child.learnosityItemReference];
                          let itemResponse = isPreApSubject
                            ? itemResult
                              ? itemResult.score
                              : null
                            : (scoringRubricScoresByUser[student.id] || {})[
                                child.learnosityItemReference
                              ];

                          if (
                            itemResponse &&
                            typeof itemResponse === 'object' &&
                            !Array.isArray(itemResponse)
                          ) {
                            itemResponse = itemResponse.score;
                          }

                          if (showTwoColumnResults) {
                            const [studentData, teacherData] =
                              getTwoColumnTableData(
                                student.id,
                                child.learnosityItemReference,
                              );

                            return (
                              <td className="student_scoring_data">
                                <div className="student_scoring_data_items">
                                  <div className={studentData.className}>
                                    {studentData.score}
                                  </div>
                                  <div className={teacherData.className}>
                                    {teacherData.score}
                                  </div>
                                </div>
                              </td>
                            );
                          } else if (showStudentColumnResults) {
                            return getSingleColumnTableData(
                              studentResultsByUsers,
                              student.id,
                              child.learnosityItemReference,
                            );
                          } else if (showTeacherColumnResults) {
                            return getSingleColumnTableData(
                              teacherResultsByUsers,
                              student.id,
                              child.learnosityItemReference,
                            );
                          }

                          if (itemResult) {
                            let label =
                              itemResponse === null ||
                              typeof itemResponse === 'undefined'
                                ? NO_ANSWER_LABEL
                                : itemResponse;
                            return (
                              <td
                                key={child.learnosityItemReference}
                                className={
                                  isPreApSubject
                                    ? undefined
                                    : itemResult.correct
                                      ? 'correct'
                                      : 'incorrect'
                                }
                                style={
                                  isPreApSubject ? preapResultsStyle : undefined
                                }
                              >
                                {label}
                              </td>
                            );
                          }
                          return (
                            <td
                              key={child.learnosityItemReference}
                              style={
                                isPreApSubject ? preapResultsStyle : undefined
                              }
                            >
                              {studentScoringCondition
                                ? NO_ANSWER_LABEL
                                : NO_RESULTS_LABEL}
                            </td>
                          );
                        });
                      }

                      let label = studentScoringCondition
                        ? NO_ANSWER_LABEL
                        : NO_RESULTS_LABEL;
                      const results =
                        (itemResultsByUser[student.id] || {}).items || {};
                      const itemResult = results[item.learnosityItemReference];
                      const itemResponse = (responsesByUser[student.id] || {})[
                        item.learnosityItemReference
                      ];
                      const isStudentScoring =
                        showTwoColumnResults ||
                        showStudentColumnResults ||
                        showTeacherColumnResults;
                      const classNames = {
                        student_scoring_data: isStudentScoring,
                        no_data: !itemResult,
                        correct: itemResult && itemResult.correct,
                        incorrect: itemResult && !itemResult.correct,
                      };

                      if (itemResult) {
                        if (itemResponse) {
                          // Now we just check that the reference is in the list of TEIs
                          const isTEI = listOfTEIs.includes(
                            item.learnosityItemReference,
                          );
                          classNames.tei = isTEI;
                          if (isTEI) {
                            label =
                              icons[
                                itemResult.correct ? 'correct' : 'incorrect'
                              ];
                          } else if (itemResponse.length) {
                            label = itemResponse.join(',');
                          } else {
                            label = NO_ANSWER_LABEL;
                          }
                        }
                        if (isTimeView()) {
                          label =
                            (itemResult.time_spent &&
                              secondsToTime(itemResult.time_spent)) ||
                            '-';
                        }
                      }

                      if (isStudentScoring) {
                        label = (
                          <div className="student_scoring_data_items">
                            {label}
                          </div>
                        );
                      }

                      return (
                        <td
                          key={item.learnosityItemReference}
                          className={cn(classNames)}
                        >
                          {label}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

StudentPerformanceTable.propTypes = {
  items: PropTypes.array.isRequired,
  params: PropTypes.shape({
    assignmentId: PropTypes.string,
    subject: PropTypes.string,
  }).isRequired,
  results: PropTypes.array.isRequired,
  assignment: PropTypes.shape({
    timer: PropTypes.number,
  }).isRequired,
  hidePointsEarned: PropTypes.bool,
  showScoringDropdown: PropTypes.bool,
  selectedScoringDropdownItem: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  setDropdownPosition: PropTypes.func,
  questionPreviewProps: PropTypes.object,
};

StudentPerformanceTable.defaultProps = {
  showScoringDropdown: false,
  submittedDateStudentMatrix: true,
  selectedScoringDropdownItem: { label: '', value: '' },
  setDropdownPosition: () => {},
  performanceTiers: { tiersData: { performanceTiers: [] } },
  questionPreviewProps: {},
};

export default connect((state) => ({
  userId: state.userData.me.initId,
  isPreApSubject: state.subject.data.isPreApSubject,
  pacingFlag: state.featureFlag.pacing,
  resultsDistractorsFlag: state.featureFlag.results_distractors,
  allowLateSubmissionFF: state.featureFlag.allow_late_submission,
  userData: state.userData,
}))(StudentPerformanceTable);
